// verification-recipe-runner.ts
//
// Runs ONE verification-profile recipe (schemas/verification-schema.ts's
// `VerificationRecipeSchema`: argv/cwd/env_allowlist/report_parser/
// report_glob) via `child_process.spawn` directly — never a shell string,
// so a report path or argv element cannot inject a second command (§2:
// "Recipe chỉ từ verification-profile đã duyệt — không chạy shell tuỳ ý").
//
// The controller keeps credentials OUT of this subprocess's env: only keys
// literally named in `recipe.env_allowlist` are copied from the caller's
// candidate env (mirrors child-env-allowlist-builder.ts's allowlist-only
// pattern, but keyed off the recipe's own list rather than a fixed set,
// since recipes vary per adapter/kind).
import { spawn } from "node:child_process";
import { existsSync } from "node:fs";
import { join } from "node:path";
import {
  registerRecipeProcessGroup,
  unregisterRecipeProcessGroup,
} from "./active-recipe-process-groups.js";
import { terminateProcessGroup } from "./process-group-reaping.js";
import type { VerificationProfile } from "./schemas/verification-schema.js";
import { SecretRedactor } from "./secret-redactor.js";

// Recipe shape borrowed structurally from the schema's `recipes` element
// (schemas/verification-schema.ts keeps `VerificationRecipeSchema` module-
// private and only exports the profile type) — this stays in sync
// automatically since it is derived, not duplicated by hand.
export type VerificationRecipe = VerificationProfile["recipes"][number];

export interface RunVerificationRecipeInput {
  recipe: VerificationRecipe;
  /** Candidate env the caller has available; only allowlisted keys are forwarded. */
  env: Record<string, string | undefined>;
  timeoutMs: number;
  /** After the timeout's SIGTERM, how long the group gets before SIGKILL (CXR-01). */
  killGraceMs?: number;
  secretValues?: string[];
}

export interface RunVerificationRecipeResult {
  exitCode: number | null;
  timedOut: boolean;
  reportPath: string;
  reportExists: boolean;
  stdoutTail: string;
  stderrTail: string;
  /** Only when `timedOut`: false ⇒ the group was still alive after SIGKILL + reap wait. */
  groupReaped?: boolean;
  /** The signal that ended the recipe when it has no exit code (review P2 L5). */
  signal?: NodeJS.Signals;
}

const TAIL_MAX_CHARS = 8_000;
const DEFAULT_KILL_GRACE_MS = 5_000;
// After the recipe exits, how long a process it left holding our pipes may
// keep them before we close our ends (review P1 M1).
const PIPE_DRAIN_MS = 500;
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function buildAllowlistedEnv(
  candidate: Record<string, string | undefined>,
  allowlist: string[],
): Record<string, string> {
  const env: Record<string, string> = {};
  for (const key of allowlist) {
    const value = candidate[key];
    if (value !== undefined) env[key] = value;
  }
  return env;
}

function tail(text: string, redactor: SecretRedactor): string {
  const redacted = redactor.feed(text) + redactor.flush();
  return redacted.length > TAIL_MAX_CHARS ? redacted.slice(-TAIL_MAX_CHARS) : redacted;
}

/**
 * Spawn `recipe.argv[0]` with `recipe.argv.slice(1)` as args, cwd inside the
 * mission worktree, env restricted to `recipe.env_allowlist`. The recipe is
 * its own process group (CXR-01): past `timeoutMs` the WHOLE group gets
 * SIGTERM, then SIGKILL after `killGraceMs`, and the result is only returned
 * once the group is gone — a deploy wrapper's grandchild cannot keep acting
 * after `timedOut`. A recipe that exits on its own is decided by that exit:
 * a background process it left holding our pipes (`daemon & exit 0`) is not
 * killed and does not turn the run into a timeout — after PIPE_DRAIN_MS we
 * close our ends of the pipes instead. Never reads `argv`/`env` from
 * the worker's result — the caller must have already resolved `recipe` from
 * an APPROVED verification-profile before calling this function.
 */
export function runVerificationRecipe(
  input: RunVerificationRecipeInput,
): Promise<RunVerificationRecipeResult> {
  const { recipe } = input;
  const [command, ...args] = recipe.argv;
  if (command === undefined) {
    throw new Error("verification-recipe-runner: recipe.argv rỗng.");
  }
  const env = buildAllowlistedEnv(input.env, recipe.env_allowlist);
  const reportPath = join(recipe.cwd, recipe.report_glob);
  const stdoutRedactor = new SecretRedactor({ secretValues: input.secretValues ?? [] });
  const stderrRedactor = new SecretRedactor({ secretValues: input.secretValues ?? [] });

  return new Promise<RunVerificationRecipeResult>((resolve, reject) => {
    const child = spawn(command, args, {
      cwd: recipe.cwd,
      env,
      stdio: ["ignore", "pipe", "pipe"],
      detached: true,
    });
    // detached ⇒ the child leads its own group, pgid === pid (POSIX).
    const pgid = child.pid;
    if (pgid !== undefined) registerRecipeProcessGroup(pgid);
    let stdout = "";
    let stderr = "";
    let timedOut = false;
    let settled = false;
    let exitCode: number | null | undefined;
    let exitSignal: NodeJS.Signals | null = null;
    let groupStopped: Promise<{ groupReaped: boolean } | undefined> = Promise.resolve(undefined);

    const timer = setTimeout(() => {
      timedOut = true;
      if (pgid !== undefined) {
        groupStopped = terminateProcessGroup(
          pgid,
          input.killGraceMs ?? DEFAULT_KILL_GRACE_MS,
          sleep,
        );
      }
    }, input.timeoutMs);

    child.stdout?.on("data", (chunk: Buffer) => {
      stdout += chunk.toString("utf8");
    });
    child.stderr?.on("data", (chunk: Buffer) => {
      stderr += chunk.toString("utf8");
    });

    child.on("error", (err) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (pgid !== undefined) unregisterRecipeProcessGroup(pgid);
      reject(err);
    });

    // The leader's exit decides the run; 'close' also waits for every holder
    // of our pipes, which a left-behind background process may never release.
    child.on("exit", (code, signal) => {
      exitCode = code;
      exitSignal = signal;
      clearTimeout(timer);
      setTimeout(() => {
        child.stdout?.destroy();
        child.stderr?.destroy();
      }, PIPE_DRAIN_MS).unref();
    });

    child.on("close", async (code) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      // Timed out: the leader closing is not the group stopping — wait for it.
      const stopped = await groupStopped;
      if (pgid !== undefined) unregisterRecipeProcessGroup(pgid);
      resolve({
        exitCode: exitCode ?? code,
        timedOut,
        reportPath,
        reportExists: existsSync(reportPath),
        stdoutTail: tail(stdout, stdoutRedactor),
        stderrTail: tail(stderr, stderrRedactor),
        ...(stopped === undefined ? {} : { groupReaped: stopped.groupReaped }),
        ...(exitSignal === null ? {} : { signal: exitSignal }),
      });
    });
  });
}
